import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "../Home/HomePage.module.css"; // adjust to your module

const API =  "https://simplix.co.za/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // If no token in URL, redirect to forgot-password
  if (!token) {
    return (
      <div className={styles.authShell}>
        <div className={styles.authCard}>
          <div className={styles.authHeader}>
            <h2>Invalid link</h2>
          </div>
          <p>This password reset link is missing or invalid. Please request a new one.</p>
          <div className={styles.authHelp}>
            <button className={styles.primaryBtn} onClick={() => navigate("/forgot-password")}>
              Request a new reset link
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Password strength indicator ──────────────────────────────────────
  const strength = getPasswordStrength(newPassword);

  function getPasswordStrength(pw) {
    if (!pw) return { score: 0, label: "", color: "#ccc" };
    let score = 0;
    if (pw.length >= 10) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const labels = ["Very weak", "Weak", "Fair", "Good", "Strong", "Very strong"];
    const colors = ["#e74c3c", "#e67e22", "#f1c40f", "#2ecc71", "#27ae60", "#2ecc71"];
    return { score, label: labels[score] || "Very strong", color: colors[Math.min(score, 5)] };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const r = await fetch(`${API}/auth/reset-password/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: newPassword }),
      });

      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        if (r.status === 400 && data.missing) {
          setError(`Password requirements not met: ${data.missing.join(", ")}`);
        } else if (r.status === 400 && data.error === "token_expired_or_invalid") {
          setError("This link has expired or was already used. Please request a new reset.");
        } else {
          setError(data.message || "Something went wrong. Please try again.");
        }
        return;
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className={styles.authShell}>
        <div className={styles.authCard}>
          <div className={styles.authHeader}>
            <h2>✅ Password updated</h2>
          </div>
          <p>Your password has been successfully changed. You can now log in with your new password.</p>
          <div className={styles.authHelp}>
            <button className={styles.primaryBtn} onClick={() => navigate("/login")}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.authShell}>
      <div className={styles.authCard}>
        <div className={styles.authHeader}>
          <p className={styles.sectionEyebrow}>Set new password</p>
          <h2>Reset your password</h2>
          <p>Enter and confirm your new password below.</p>
        </div>

        <form className={styles.authForm} onSubmit={handleSubmit}>
          {/* New password */}
          <div style={{ position: "relative" }}>
            <input
              className={styles.authInput}
              type={showPassword ? "text" : "password"}
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={10}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              {showPassword ? "🙈" : "👁️"}
            </button>
          </div>

          {/* Strength bar */}
          {newPassword && (
            <div style={{ margin: "4px 0 12px" }}>
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  background: strength.color,
                  transition: "background 0.3s",
                }}
              />
              <small style={{ color: strength.color }}>{strength.label}</small>
            </div>
          )}

          {/* Confirm password */}
          <input
            className={styles.authInput}
            type={showPassword ? "text" : "password"}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={10}
          />

          {/* Requirements hint */}
          <div style={{ fontSize: 12, color: "#666", margin: "8px 0", lineHeight: 1.6 }}>
            <strong>Password requirements:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
              <li>Minimum 10 characters</li>
              <li>At least one uppercase letter</li>
              <li>At least one lowercase letter</li>
              <li>At least one digit</li>
              <li>At least one special character</li>
              <li>No spaces</li>
            </ul>
          </div>

          {error && <div className={styles.authError}>{error}</div>}

          <div className={styles.authHelp}>
            <button
              className={styles.primaryBtn}
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
            >
              {loading ? "Updating…" : "Set new password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}